import * as queryString from 'query-string'
import { LoaderBackdrop } from 'asterX-UI/Loaders'
import jwt_decode from 'jwt-decode'
import { AuthService } from 'lib/api'
import { PROJECT_CODE } from 'lib/constants/roles'
import { useResErrors, useRoutes } from 'lib/hooks'
import { useModalbar } from 'lib/modules/Modalbar'
import { deepCopyObject, hasRoleByCode, hasRoleByPosition, LocalStorageService, SessionStorageService } from 'lib/utils'
import { createContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { HOME_PATH, SIGN_IN_PATH } from 'routes/constants.routes'
import { useMediaQuery } from '@material-ui/core'

const initialState = {
  token: '',
  role: null,
  data: {}
}

export const AuthStoreContext = createContext({
  authStore: {},
  authStoreFunc: {}
})
const AuthStoreProvider = props => {
  const { location } = props
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'))

  const history = useHistory()
  const routes = useRoutes()

  const currentPathname = useRef(history.location.pathname)
  const currentSearch = useRef(history.location.search)

  const { t } = useTranslation()
  const { standardCheck } = useResErrors()
  const { callModalbar } = useModalbar()

  const _isMounted = useRef(true)

  const [appBar, setAppBar] = useState(true)
  const [user, setUser] = useState(initialState)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [loading, setLoading] = useState(false)
  const [initLoading, setInitLoading] = useState(false)

  useEffect(() => {
    initEffect()

    return () => {
      _isMounted.current = false
    }
  }, [])

  const initEffect = async () => {
    const values = queryString.parse(location?.search)
    if (values && values.token) {
      await setTokenFromQuery(values.token)
      setAppBar(false)
    }

    if (user?.token) {
      await setTokenFromQuery(user.token)
    } else {
      await setTokenFromLS()
    }
  }

  const logout = () => {
    AuthService.setToken(null)
    SessionStorageService.clear()
    setUser(deepCopyObject(initialState))
    setIsLoggedIn(false)
    history.push(SIGN_IN_PATH)
  }

  const getUserRole = roles => (roles ? roles?.find(el => el?.project?.code.includes(PROJECT_CODE)) : null)
  const isThisProject = user => user?.roleList?.some(el => el?.project?.code.includes(PROJECT_CODE))

  const setDataToStorage = async (token = '', isReset = false) => {
    AuthService.setToken(token)
    let role = null
    if (isReset) {
      setUser(deepCopyObject(initialState))
      setIsLoggedIn(false)
      return
    }
    setInitLoading(true)

    try {
      await AuthService.getUserByToken().then(res =>
        standardCheck(
          res,
          async () => {
            setUser(prevState => ({
              ...prevState,
              token,
              data: res.data,
              role: getUserRole(res.data?.roles)
            }))

            setIsLoggedIn(!!token && !!res?.data?.id)
          },
          {
            onFail: () => {
              role = null
              setIsLoggedIn(false)
              AuthService.setToken()
              SessionStorageService.clear()
              routes.goTo(SIGN_IN_PATH)
            }
          }
        )
      )
    } catch (error) {
      console.error(error)
    } finally {
      setInitLoading(false)
    }

    return role
  }

  const setTokenFromQuery = async token => {
    if (!token) {
      setInitLoading(false)
      return false
    }
    try {
      const decoded = jwt_decode(token)
      if (decoded && isThisProject(decoded)) {
        setIsLoggedIn(true)

        routes.goTo(routes.lastVisit ? (routes.lastVisit !== SIGN_IN_PATH ? routes.lastVisit : HOME_PATH) : HOME_PATH)
        await setDataToStorage(token, false)

        return true
      } else {
        await setDataToStorage(null, true)
        return false
      }
    } catch (e) {
      return false
    } finally {
      setInitLoading(false)
    }
  }

  const setTokenFromLS = () => setTokenFromQuery(LocalStorageService.getAccessToken())

  const login = userData => {
    if (!userData?.username?.length || !userData?.password?.length) {
      return
    }

    SessionStorageService.clear()

    AuthService.login(userData).then(res =>
      standardCheck(
        res,
        async () => {
          const { token } = res.data
          const decoded = jwt_decode(token)

          if (!!token && !!decoded && isThisProject(decoded)) {
            setIsLoggedIn(true)
            LocalStorageService.setAccessToken(token)
            setDataToStorage(token)
            routes.goTo(HOME_PATH)
          } else {
            callModalbar(t('errors.NotAccess_SignIn.title'), t('errors.NotAccess_SignIn.body'), { btnText: 'OK' })
          }
        },
        {
          onErrCustom: () => {
            callModalbar(t('errors.Unauthorized_SignIn.title'), t('errors.Unauthorized_SignIn.body'), { btnText: 'OK' })
          }
        }
      )
    )
  }

  const isAdmin = () => hasRoleByCode('ADMIN', user.data)
  const isManager = () => hasRoleByCode('MANAGER', user.data)
  const isLawyer = () => hasRoleByCode('LAWYER', user.data)
  const isSoftManager = () => hasRoleByPosition('MANAGER', user.data, 'SOFT')

  useEffect(() => {
    routes.setLastVisit(location?.pathname)
  }, [location?.pathname])

  useEffect(() => {
    const unlisten = history.listen((newLocation, action) => {
      if (action === 'PUSH') {
        if (newLocation.pathname !== currentPathname.current || newLocation.search !== currentSearch.current) {
          currentPathname.current = newLocation.pathname
          currentSearch.current = newLocation.search

          history.push({
            pathname: newLocation.pathname,
            search: newLocation.search
          })
        }
      } else {
        history.go(1)
      }
    })

    return () => {
      unlisten()
    }
  }, [history])

  const authStore = {
    appBar,
    user,
    loading,
    isLoggedIn,
    isMobile
  }

  const authStoreFunc = {
    login,
    setLoading,
    logout,
    setTokenFromQuery,
    setTokenFromLS,
    isAdmin,
    isLawyer,
    isManager,
    isSoftManager
  }

  return (
    <AuthStoreContext.Provider value={{ authStore, authStoreFunc }}>
      {initLoading ? <LoaderBackdrop loading={initLoading} /> : <>{props.children}</>}
      <LoaderBackdrop loading={loading} />
    </AuthStoreContext.Provider>
  )
}
export default AuthStoreProvider
